// Top-level layout: sticky header with brand, nav links, theme toggle.
import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Sun, Moon, Cards, Plus, ListBullets, ChartLineUp } from '@phosphor-icons/react';
import { useTheme } from '../contexts/ThemeContext';
import { NAV } from '../constants/testIds';

const linkBase =
  'group flex items-center gap-2 px-4 py-2.5 border border-transparent text-sm tracking-wide transition-all duration-200';
const linkActive =
  'border-foreground bg-foreground text-background';
const linkIdle =
  'hover:border-foreground/40 hover:bg-foreground/[0.04] text-foreground/70 hover:text-foreground';

function NavItem({ to, end, label, icon: Icon, testId }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
    >
      <Icon size={18} weight="duotone" />
      <span className="font-medium">{label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { theme, toggle } = useTheme();
  const loc = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* HEADER */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <NavLink to="/" className="flex items-center gap-3 group">
            <span className="font-display font-black text-2xl tracking-tighter leading-none">
              DE<span className="text-primary">/</span>TR
            </span>
            <span className="hidden sm:block overline border-l border-border pl-3">
              B2 Vokabel · Karteikarten
            </span>
          </NavLink>

          <nav className="flex items-center gap-1.5">
            <NavItem to="/" end label="Çalış" icon={Cards} testId={NAV.studyLink} />
            <NavItem to="/add" label="Ekle" icon={Plus} testId={NAV.addLink} />
            <NavItem to="/list" label="Liste" icon={ListBullets} testId={NAV.listLink} />
            <NavItem to="/dashboard" label="Panel" icon={ChartLineUp} testId={NAV.dashboardLink} />
          </nav>

          <button
            data-testid={NAV.themeToggle}
            onClick={toggle}
            aria-label="Tema değiştir"
            className="w-10 h-10 grid place-items-center border border-border hover:border-foreground btn-tactile"
          >
            {theme === 'dark'
              ? <Sun size={18} weight="duotone" />
              : <Moon size={18} weight="duotone" />}
          </button>
        </div>
        {/* Editorial sub-bar */}
        <div className="border-t border-border bg-foreground text-background">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 h-8 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase font-mono-plex">
            <span>Deutsch · Türkçe · B2</span>
            <span className="hidden md:inline">Route: {loc.pathname === '/' ? 'studienraum' : loc.pathname.slice(1)}</span>
            <span>v1.0 — KI-gestützt</span>
          </div>
        </div>
      </header>

      {/* MAIN */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* FOOTER */}
      <footer className="border-t border-border mt-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between text-xs text-muted-foreground">
          <span className="font-mono-plex tracking-wider">© Karteikarten Studio</span>
          <span className="overline">Claude · Sonnet 4.5</span>
        </div>
      </footer>
    </div>
  );
}
